import styled from 'styled-components';

export const DashboardContainer = styled.div`
  background: var(--bg-section);
  min-height: calc(100vh - 68px);
  padding: 40px 20px 72px;

  > * {
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const WelcomeSection = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .eyebrow {
    display: inline-block;
    background: var(--primary-subtle);
    color: var(--primary);
    font-size: var(--text-xs);
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: var(--radius-round);
    margin-bottom: 10px;
  }

  h1 {
    font-size: var(--text-3xl);
    letter-spacing: -0.5px;
    margin-bottom: 2px;
  }

  p {
    color: var(--text-secondary);
  }

  .badge {
    font-size: var(--text-sm);
    font-weight: 700;
    padding: 8px 16px;
    border-radius: var(--radius-round);

    &.verified {
      background: var(--success-subtle);
      color: var(--success-dark);
      border: 1px solid var(--success-light);
    }

    &.pending {
      background: var(--warning-subtle);
      color: var(--warning-dark);
      border: 1px solid var(--warning-border);
    }
  }
`;

export const VerificationNotice = styled.div`
  background: var(--warning-subtle);
  border: 1px solid var(--warning-border);
  border-radius: var(--radius-lg);
  padding: 16px 20px;
  margin-bottom: 24px;
  font-size: var(--text-sm);

  strong {
    color: var(--warning-dark);
    display: block;
    margin-bottom: 4px;
  }

  p { color: var(--text-secondary); }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 36px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const StatCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 22px;
  box-shadow: var(--shadow-xs);
  border-top: 3px solid ${props => (props.$money ? 'var(--success)' : 'var(--primary)')};
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
`;

export const StatNumber = styled.div`
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 4px;
`;

export const StatLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

export const QuickActions = styled.section`
  margin-bottom: 36px;

  h2 {
    font-size: var(--text-xl);
    margin-bottom: 16px;
  }

  .actions-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;

    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 480px) { grid-template-columns: 1fr; }
  }
`;

export const ActionCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  text-align: left;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 18px;
  cursor: pointer;
  font-family: var(--font-brand);
  box-shadow: var(--shadow-xs);
  transition: transform var(--transition-fast), box-shadow var(--transition-fast), border-color var(--transition-fast);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-hover);
    border-color: var(--border-medium);
  }

  .icon {
    font-size: 26px;
    background: var(--primary-subtle);
    border-radius: var(--radius-md);
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  h3 {
    font-size: var(--text-sm);
    font-family: var(--font-brand);
    font-weight: 700;
    margin-bottom: 3px;
  }

  p {
    font-size: var(--text-xs);
    color: var(--text-secondary);
    line-height: 1.4;
  }
`;

export const RecentActivity = styled.section`
  h2 {
    font-size: var(--text-xl);
    margin-bottom: 16px;
  }
`;

export const ActivityList = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
`;

export const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-light);
  transition: background var(--transition-fast);

  &:last-child { border-bottom: none; }

  &:hover { background: var(--bg-section); }

  .activity-icon {
    font-size: 20px;
    flex-shrink: 0;
  }

  .activity-content {
    flex: 1;
    min-width: 0;

    .activity-message {
      font-size: var(--text-sm);
      font-weight: 500;
      color: var(--text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .activity-time {
      font-size: var(--text-xs);
      color: var(--text-muted);
    }
  }

  .activity-status {
    font-size: 12px;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  background: var(--bg-card);
  border: 1px dashed var(--border-medium);
  border-radius: var(--radius-lg);

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
  }
`;

export const PublicProfile = styled.section`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  padding: 20px;
  margin-bottom: 36px;
  min-width: 0;

  h2 {
    font-size: var(--text-xl);
    margin-bottom: 6px;
  }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    margin-bottom: 14px;
    overflow-wrap: anywhere;
  }

  a.profile-link {
    color: var(--primary);
    font-weight: 600;
  }
`;
